import { useCallback, useState, useMemo, useEffect } from "react";
import styles from "./GenerateExitTicketLinkModal.module.scss";
import "./customStyle.css";
import Modal from "src/components/modal/Modal";
import Button from "src/components/button/Button";
import GlassIcon from "@src/assets/icons/hourglass.svg?react";
import CloseIcon from "@src/assets/icons/close.svg?react";
import { TicketInput } from "./text-input/TicketInput";
import {
  TextInput,
  TextInputValue,
} from "@src/components/text-input/TextInput";
import { Slider } from "antd";
import {
  useCourseContent,
  useAnalytics,
  Events,
  useUser,
  useQueryDictionary,
} from "@src/hooks";
import {
  FeedbackFormContent,
  useCreateFeedbackFormMutation,
  SllLesson,
  SllCourse,
  Quiz,
  useCreateQuizFeedbackFormMutation,
  useSslCreatePreProgramFeedbackFormMutation,
} from "@src/api/graphql-codegen";
import {
  FeedbackForm,
  ExitTicketActive,
  SurveyTypes,
  SelectOption,
} from "@src/types";
import {
  SURVEY_TYPE,
  auProgramGradeLevels,
  usProgramGradeLevels,
} from "@src/constants";
import Select from "@src/components/select/Select";
import Spinner from "@src/components/spinner/Spinner";
import { useStandaloneContent } from "@src/hooks/useStandaloneContent";
import { isJSON } from "@src/utils/validation";
import { logger } from "../../../utils/logger";

type Props = {
  open: boolean;
  formData?: string | null;
  onClose: () => void;
  onActiveExitTicket: (
    feedback: FeedbackFormContent,
    expireTime: any,
    ticketCount?: any,
  ) => void;
  isSll?: boolean;
  sllLesson?: SllLesson;
  sllCourse?: SllCourse;
  exitTickets?: ExitTicketActive[];
  quizType?: boolean;
  currentQuiz?: Quiz;
  ausPreProgram?: boolean;
  gradeName?: string;
};

const validationErrors = {
  tickets: "",
  minutes: "",
  className: "",
};

type ErrorKeys = keyof typeof validationErrors;

function GenerateExitTicketLinkModal(props: Props) {
  const {
    open,
    onClose,
    onActiveExitTicket,
    isSll,
    sllLesson,
    quizType = false,
    currentQuiz,
    ausPreProgram = false,
    gradeName,
  } = props;
  const {
    program,
    lesson,
    currentUnit,
    exitTickets,
    content: contentValue,
  } = useCourseContent();
  const { mcId, mcName, collectionId, collectionName, programName, programId } =
    useStandaloneContent();
  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const defaultNumberOfExitTickets = 30;

  const [tickets, setTickets] = useState(defaultNumberOfExitTickets);
  const [duration, setDuration] = useState(ausPreProgram ? 45 : 30);
  const [timeLabel, setTimeLabel] = useState("Minutes");
  const [closeMenu, setCloseMenu] = useState(false);
  const [existTicket, setExistTicket] = useState(false);
  const [errors, setErrors] =
    useState<Partial<Record<ErrorKeys, string>>>(validationErrors);
  const [beError, setBeError] = useState("");

  const formData = useMemo(() => {
    if (props.formData) {
      return props.formData;
    }
    return exitTickets.find((ticket) => ticket.lessonNum === lesson!.number)
      ?.formData;
  }, [exitTickets, lesson, props.formData]);

  // Post-Unit Assessment
  const [grade, setGrade] = useState("");

  // Pre-Program Quiz
  const [className, setClassName] = useState("");
  const [step, setStep] = useState(ausPreProgram ? 1 : 0);

  const coursesOptions = useQueryDictionary({ dictionary: "Programs" });
  const isAUuser = useMemo(
    () => user?.id?.toLowerCase().startsWith("au"),
    [user?.id],
  );

  const jsonFormData = useMemo(() => {
    if (!formData) return null;
    try {
      return JSON.parse(formData) as FeedbackForm;
    } catch (e) {
      logger.info("parse error-", e, formData);
      return null;
    }
  }, [formData]);

  const exitTicketType = useMemo(
    () =>
      Number(jsonFormData?.surveyType) === SURVEY_TYPE.EXIT_TICKET
        ? true
        : false,
    [jsonFormData],
  );
  const preProgramType = useMemo(
    () =>
      Number(jsonFormData?.surveyType) === SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ
        ? true
        : false,
    [jsonFormData],
  );
  const postProgramType = useMemo(
    () =>
      Number(jsonFormData?.surveyType) === SURVEY_TYPE.NA_POST_PROGRAM_QUIZ
        ? true
        : false,
    [jsonFormData],
  );
  const surveyType = useMemo(() => {
    let surveyTypeInt = Number(jsonFormData?.surveyType);
    switch (surveyTypeInt) {
      case 1:
        return SurveyTypes.ExitTickets;
      case 2:
        return SurveyTypes.Research;
      case 3:
        return SurveyTypes.PreProgram;
      case 4:
        return SurveyTypes.StudentQuiz;
      case 5:
        return SurveyTypes.PreProgram; // AU_PRE_PROGRAM_QUIZ
      case 6:
        return SurveyTypes.PostProgram; // POST_PROGRAM_QUIZ
      case 7:
        return SurveyTypes.AbsentStudent; // ABSENT_STUDENT
      default:
        return SurveyTypes.ExitTickets; // Default to Exit Tickets
    }
  }, [jsonFormData]);

  const [createForm, { loading }] = useCreateFeedbackFormMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      alert("Something went wrong.");
    },
    onCompleted(res) {
      const _d = {
        ...Events.mappers.program(program),
        ...Events.mappers.lesson(lesson),
        ...Events.mappers.content(contentValue),
        school_name: user?.schools?.[0]?.name || "",
        customer_name: user?.customer?.name || "",
        survey_type: surveyType,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      };

      trackEvent(Events.actions.surveyFormLinkCreated, _d);

      const expiration = timeLabel === "Minutes" ? duration : duration * 60;
      onActiveExitTicket(
        res.createFeedbackForm as FeedbackFormContent,
        expiration,
        tickets,
      );
      setTimeout(() => onClose(), 0);
    },
  });
  const [createQuizFeedback, { loading: quizLoading }] =
    useCreateQuizFeedbackFormMutation({
      onError(e) {
        logger.debug(`[e]`, e);
        alert("Something went wrong.");
      },
      onCompleted(res) {
        const expiration = timeLabel === "Minutes" ? duration : duration * 60;
        onActiveExitTicket(
          res.createQuizFeedbackForm as FeedbackFormContent,
          expiration,
          tickets,
        );
        setTimeout(() => onClose(), 0);
      },
    });

  const [sslPreProgramFeedback, { loading: preprogramLoading }] =
    useSslCreatePreProgramFeedbackFormMutation({
      onError(e) {
        logger.debug(`[e]`, e);
        alert("Something went wrong.");
      },
      onCompleted(res) {
        const expiration = timeLabel === "Minutes" ? duration : duration * 60;
        onActiveExitTicket(
          res.sslCreatePreProgramFeedbackForm as FeedbackFormContent,
          expiration,
          tickets,
        );
        setTimeout(() => onClose(), 0);
      },
    });

  const handleChangeText = useCallback(
    (setter: any, key: ErrorKeys) => (value: TextInputValue) => {
      setErrors({
        ...errors,
        [key]: "",
      });
      if (key === "tickets") {
        if (Number(value) > 0) {
          setter(Number(value));
        }
      } else {
        setter(value);
      }
    },
    [errors],
  );

  const handleSliderChange = useCallback(
    (setter: any) => (value: any) => {
      setter(Number(value));
    },
    [],
  );

  const handleChangeLabel = (value: any) => {
    setTimeLabel(value);
  };

  const handleCloseTimeMenu = () => {
    setCloseMenu((current) => !current);
  };

  const handleContinue = useCallback(
    async (e) => {
      e.preventDefault();

      if (!existTicket) {
        setStep(3);
        setExistTicket(true);
        return;
      }
      setBeError("");

      const expiration = timeLabel === "Minutes" ? duration : duration * 60;
      if (quizType) {
        const gradeId = coursesOptions.find((program) =>
          program.label.includes(grade),
        )?.value;
        await createQuizFeedback({
          variables: {
            input: {
              id: currentQuiz?.id || "",
              programId: gradeId,
              programName: grade,
              exitTicketCount: tickets,
              expiryMinutes: expiration,
              className: className,
              grade:
                grade !== ""
                  ? grade.replace(/\s*Program/, "")
                  : (gradeName?.replace(/\s*Program/, "") ?? ""),
            },
          },
        });
        return;
      } else if (preProgramType || postProgramType) {
        const gradeId = coursesOptions.find((program) =>
          program.label.includes(grade),
        )?.value;
        await createForm({
          variables: {
            input: {
              lessonName: lesson!.name,
              programId: gradeId,
              programName: grade,
              lessonId: lesson!.id,
              formData,
              unitId: currentUnit?.id,
              unitName: currentUnit?.name,
              exitTicketCount: tickets,
              expiryMinutes: expiration,
              className: className,
              grade:
                grade !== ""
                  ? grade.replace(/\s*Program/, "")
                  : (gradeName?.replace(/\s*Program/, "") ?? ""),
            },
          },
        });
        return;
      } else if (isSll) {
        if (ausPreProgram && gradeName) {
          const year = coursesOptions.find((program) =>
            program.label.includes(gradeName),
          );
          await sslPreProgramFeedback({
            variables: {
              input: {
                programId: year?.value ?? "",
                programName: year?.label ?? "",
                exitTicketCount: tickets,
                expiryMinutes: expiration,
                className: className,
                grade:
                  grade !== ""
                    ? grade.replace(/\s*Program/, "")
                    : (gradeName?.replace(/\s*Program/, "") ?? ""),
              },
            },
          });
          return;
        } else {
          await createForm({
            variables: {
              input: {
                lessonName: sllLesson?.name,
                lessonId: sllLesson?.id,
                programId: programId,
                programName: programName,
                formData,
                exitTicketCount: tickets,
                expiryMinutes: expiration,
                isSLL: true,
                unitName: "Collection",
                mcId,
                mcName,
                collectionId,
                collectionName,
                grade:
                  grade !== ""
                    ? grade.replace(/\s*Program/, "")
                    : (gradeName?.replace(/\s*Program/, "") ?? ""),
              },
            },
          });
          return;
        }
      } else {
        await createForm({
          variables: {
            input: {
              programName: program?.name,
              lessonName: lesson?.name,
              programId: program?.id,
              lessonId: lesson?.id,
              formData,
              unitId: currentUnit?.id,
              unitName: currentUnit?.name,
              exitTicketCount: tickets,
              expiryMinutes: expiration,
              grade:
                grade !== ""
                  ? grade.replace(/\s*Program/, "")
                  : (gradeName?.replace(/\s*Program/, "") ?? ""),
            },
          },
        });
        return;
      }
    },
    [
      grade,
      gradeName,
      quizType,
      coursesOptions,
      currentQuiz,
      createQuizFeedback,
      preProgramType,
      postProgramType,
      className,
      program,
      isSll,
      existTicket,
      timeLabel,
      duration,
      createForm,
      sllLesson,
      formData,
      tickets,
      lesson,
      currentUnit?.id,
      currentUnit?.name,
      ausPreProgram,
      sslPreProgramFeedback,
      programId,
      programName,
      mcId,
      mcName,
      collectionId,
      collectionName,
    ],
  );

  const handleNext = useCallback(() => {
    if (step === 0 && grade) {
      setStep(1);
    } else if (step === 1 && className) {
      setStep(2);
    }
  }, [className, step, grade]);

  const handleChangeGrade = (group: SelectOption) => {
    setGrade(group.value);
  };

  const surveyGenerationTitle = useMemo(() => {
    return preProgramType || ausPreProgram
      ? "Generate Quiz Link"
      : exitTicketType
        ? "Generate Exit Ticket Link"
        : quizType
          ? "Generate Assessment Link"
          : "Generate Survey Link";
  }, [preProgramType, exitTicketType, quizType, ausPreProgram]);

  const surveySizeViewContent = useMemo(() => {
    return preProgramType || quizType || ausPreProgram ? (
      <>
        Select the <strong>maximum</strong> number of Students who will be
        completing this {quizType ? "Assessment" : "Quiz"} in your class. HINT:
        Select more students to be on the safe side!
      </>
    ) : (
      <>
        Select the <strong>maximum</strong> number of{" "}
        {exitTicketType ? "Exit Tickets" : "Surveys"} you will collect. This
        would typically be the number of students in your class who you expect
        to complete the survey.
      </>
    );
  }, [preProgramType, exitTicketType, quizType, ausPreProgram]);

  const surveyTimeViewContent = useMemo(() => {
    return preProgramType || quizType || ausPreProgram ? (
      <>
        Set the <strong>maximum</strong> time the{" "}
        {quizType ? "Assessment" : "Quiz"} Link will remain active. After this
        time, no further submissions can be made and you will be emailed the
        results in a single Report.
      </>
    ) : (
      <>
        Set the <strong>maximum</strong> time the{" "}
        {exitTicketType ? "Exit Tickets" : "Survey"} Link will remain active.
        After this time, no{" "}
        {exitTicketType
          ? "exit tickets can be submitted and you will be emailed the Exit Ticket Report"
          : "further surveys can be submitted"}
        .
      </>
    );
  }, [preProgramType, exitTicketType, quizType, ausPreProgram]);

  useEffect(() => {
    if (isJSON(gradeName)) {
      const grades = JSON.parse(gradeName || "[]");
      if (grades.length > 0) {
        setGrade(grades[0]);
      }
    }
  }, [gradeName]);

  const grades = useMemo(() => {
    if (isJSON(gradeName)) {
      const grades = JSON.parse(gradeName || "[]");

      return grades.map((grade: string) => {
        return {
          label: grade,
          value: grade,
        };
      });
    }
    return isAUuser ? auProgramGradeLevels : usProgramGradeLevels;
  }, [isAUuser, gradeName]);

  const modalContent = useMemo(() => {
    if (
      (quizType ||
        preProgramType ||
        postProgramType ||
        (isJSON(gradeName) && grades.length > 1) ||
        !gradeName) &&
      step === 0
    ) {
      return (
        <>
          <div className={styles.header}>
            <div className={styles.preProTitle}>
              {isAUuser ? "Select Your Year" : "Select Your Grade"}
            </div>
            <div className={styles.normalText}>
              This label will appear on the{" "}
              {ausPreProgram
                ? "Quiz"
                : isJSON(gradeName) || !gradeName
                  ? "ExitTicket"
                  : "Assessment"}{" "}
              Report to help you identify which {isAUuser ? "year" : "grade"}{" "}
              the results are for.
            </div>
          </div>
          <div className={styles.inputView}>
            <Select
              menuClassName={styles.selectorStyle}
              className={styles.gradeView}
              showSearch={false}
              inline={false}
              doNotSort={true}
              placeholder={
                isAUuser ? "Select Your Year Level" : "Select Your Grade Level"
              }
              value={grade}
              options={grades}
              onChange={handleChangeGrade}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.action}>
              <Button
                disabled={false}
                size="small"
                className={styles.startButton}
                onClick={handleNext}
                label={"CONTINUE"}
              />
            </div>
          </div>
        </>
      );
    }
    if (
      (quizType || preProgramType || postProgramType || ausPreProgram) &&
      step === 1
    ) {
      return (
        <>
          <div className={styles.header}>
            <div className={styles.preProTitle}>{"Name your Class"}</div>
            <div className={styles.normalText}>
              This label will appear on the {quizType ? "Assessment" : "Quiz"}{" "}
              Report to help you identify which class the results are for.
            </div>
          </div>
          <div className={styles.inputView}>
            <TextInput
              className={styles.classNameView}
              label={""}
              id={"name"}
              placeholder={"Class Name..."}
              value={className}
              onChange={handleChangeText(setClassName, "className")}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.action}>
              <Button
                disabled={false}
                size="small"
                className={styles.startButton}
                onClick={handleNext}
                label={"CONTINUE"}
              />
            </div>
          </div>
        </>
      );
    }
    if (!existTicket) {
      return (
        <>
          <div className={styles.header}>
            <div className={styles.title}>{surveyGenerationTitle}</div>
            <div className={styles.normalText}>{surveySizeViewContent}</div>
          </div>
          <div className={styles.editView}>
            <TicketInput
              autoFocus={true}
              hasError={false}
              subTitle={""}
              type={"number"}
              id={"tickets"}
              label={
                preProgramType ||
                quizType ||
                ausPreProgram ||
                surveyType === SurveyTypes.StudentQuiz
                  ? "Students"
                  : exitTicketType
                    ? "Exit Tickets"
                    : "Surveys"
              }
              value={tickets ?? defaultNumberOfExitTickets}
              onChange={handleChangeText(setTickets, "tickets")}
            />
            <div className={styles.sliderView}>
              <span>
                {tickets ?? defaultNumberOfExitTickets}{" "}
                {preProgramType ||
                quizType ||
                ausPreProgram ||
                surveyType === SurveyTypes.StudentQuiz
                  ? "Students"
                  : exitTicketType
                    ? "Exit Tickets"
                    : "Surveys"}
              </span>
              <Slider
                defaultValue={defaultNumberOfExitTickets}
                value={tickets ?? defaultNumberOfExitTickets}
                min={1}
                max={300}
                onChange={handleSliderChange(setTickets)}
                trackStyle={{ background: "#FFB073" }}
              />
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.action}>
              <Button
                disabled={false}
                size="small"
                className={styles.startButton}
                onClick={handleContinue}
                label={"CONTINUE"}
              />
            </div>
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className={styles.header}>
            <div className={styles.title}>{surveyGenerationTitle}</div>
            <div className={styles.normalText}>{surveyTimeViewContent}</div>
          </div>
          <div className={styles.editView}>
            <TicketInput
              autoFocus={true}
              hasError={false}
              subTitle={""}
              type={"number"}
              id={"hours"}
              label={timeLabel}
              value={duration ?? 30}
              closeMenu={closeMenu}
              onChange={handleChangeText(setDuration, "tickets")}
              setTimeChange={handleChangeLabel}
            />
            <div className={styles.sliderView}>
              <span>
                {duration ?? 30} {timeLabel}
              </span>
              <Slider
                defaultValue={ausPreProgram ? 45 : 60}
                value={duration}
                min={timeLabel === "Minutes" ? 5 : 1}
                max={timeLabel === "Minutes" ? 90 : 48}
                onChange={handleSliderChange(setDuration)}
                trackStyle={{ background: "#FFB073" }}
              />
            </div>
          </div>
          <div className={styles.safetyNote}>
            <GlassIcon width={51} height={51} />
            <span>
              <strong>Recommendation:</strong> Allow enough time for students to
              submit their{" "}
              {quizType ? "response" : ausPreProgram ? "quiz" : "surveys"}. Eg.
              If you generate the link at 8am for your class beginning at 10am,
              which runs for 30 minutes, set a duration for 3 hours.
            </span>
          </div>
          <div className={styles.content}>
            {Boolean(beError) && <div className={styles.error}>{beError}</div>}
            <div className={styles.action}>
              <div
                role="button"
                onClick={handleContinue}
                className={
                  loading || quizLoading || preprogramLoading
                    ? styles.generateLoadingBtn
                    : styles.generateBtn
                }
              >
                {loading || quizLoading || preprogramLoading ? (
                  <Spinner width={20} height={20} />
                ) : (
                  <span>
                    {preProgramType || ausPreProgram
                      ? `Activate Link for ${duration} ${timeLabel} only`
                      : exitTicketType
                        ? `Activate Exit Tickets link for ${duration} ${timeLabel} only`
                        : quizType
                          ? `Generate Assessment Link for ${duration} ${timeLabel}`
                          : `Active Survey Link for ${duration} ${timeLabel}`}
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      );
    }
  }, [
    gradeName,
    isAUuser,
    closeMenu,
    exitTicketType,
    existTicket,
    tickets,
    duration,
    beError,
    timeLabel,
    loading,
    quizLoading,
    preProgramType,
    postProgramType,
    className,
    step,
    grade,
    grades,
    quizType,
    ausPreProgram,
    preprogramLoading,
    surveyGenerationTitle,
    surveySizeViewContent,
    surveyTimeViewContent,
    surveyType,
    handleSliderChange,
    handleContinue,
    handleChangeText,
    setDuration,
    setTickets,
    handleNext,
  ]);

  return (
    <Modal
      global={false}
      open={open}
      onClose={onClose}
      closeButton={false}
      className={styles.wrapper}
    >
      <div className={styles.mainView} onClick={handleCloseTimeMenu}>
        <button onClick={onClose} className={styles.closeWrapper}>
          <CloseIcon className={styles.closeIcon} />
        </button>
        {modalContent}
      </div>
    </Modal>
  );
}

export default GenerateExitTicketLinkModal;
